import React from "react";
import { cn } from "@/lib/utils";
import {
  renderEditableCell,
  getStatusColorClasses,
  getPriorityColorClasses,
  getEntryTypeColorClasses,
  getApprovalStatusColorClasses,
  getSyncStatusColorClasses,
} from "../../utils";
import type {
  BaseEntity,
  SimpleFieldConfig,
  UniversalInlineEditHandler,
  FieldValue,
} from "../../types";
import type { SimpleTableContextValue } from "../../contexts/SimpleTableContext";
import type { AuthenticatedUser } from "@/lib/types/unified";

export interface BadgeCellProps<TData extends BaseEntity> {
  value: FieldValue;
  fieldId: string;
  fieldConfig: SimpleFieldConfig;
  item: TData;
  isEditing: boolean;
  state: SimpleTableContextValue<TData>["state"];
  callbacks: SimpleTableContextValue<TData>["callbacks"];
  auth: { appUser: AuthenticatedUser | null; roleName: string | null };
  data: TData[];
  inlineEditHandler: UniversalInlineEditHandler;
  customDisplayText?: string;
}

/**
 * BadgeCell renders badge-style cells with color classes
 * Used for: status, priority, entry_type, is_approved, sync_status
 */
export function BadgeCell<TData extends BaseEntity>({
  value,
  fieldId,
  fieldConfig,
  item,
  isEditing,
  state,
  callbacks,
  auth,
  data,
  inlineEditHandler,
  customDisplayText,
}: BadgeCellProps<TData>) {
  // Handle null/undefined values
  if (value === null || value === undefined) {
    return <span className="text-gray-400">—</span>;
  }

  // Compute colors per fieldId. Each color getter accepts a different input
  // type, so dispatch on `fieldId` rather than unifying behind a single
  // signature — keeps the call sites strongly typed without `as any`.
  const colors = ((): { bg: string; text: string; border: string } => {
    switch (fieldId) {
      case "status":
        return getStatusColorClasses(typeof value === "string" ? value : "");
      case "priority":
        return getPriorityColorClasses(typeof value === "string" ? value : "");
      case "entry_type":
        return getEntryTypeColorClasses(typeof value === "string" ? value : "");
      case "is_approved":
        return getApprovalStatusColorClasses(typeof value === "boolean" ? value : false);
      case "sync_status":
        return getSyncStatusColorClasses(typeof value === "string" ? value : "");
      default:
        return { bg: "", text: "", border: "" };
    }
  })();

  const narrowedValue: string | boolean =
    fieldId === "is_approved"
      ? typeof value === "boolean"
        ? value
        : false
      : typeof value === "string"
      ? value
      : "";

  return (
    <div
      className={cn(
        "inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium transition-all duration-200 border",
        colors.bg,
        colors.text,
        colors.border,
        fieldConfig.editable &&
          !state.editingCell &&
          "hover:shadow-md hover:scale-105 cursor-pointer"
      )}
    >
      {renderEditableCell<TData>(
        value,
        fieldConfig,
        item.id,
        fieldId,
        state.editingCell,
        state.editValue,
        callbacks.handleCellEditCallback,
        callbacks.handleCellSaveCallback,
        callbacks.handleCellCancelCallback,
        state.setEditValue,
        data,
        inlineEditHandler,
        auth.appUser || undefined,
        false, // isTitleField
        customDisplayText,
        auth.roleName || undefined, // userRole
        undefined // entityName - not needed for badge rendering
      )}
    </div>
  );
}
